import { Alert, Button, Select, Space, Table, Tag, Tooltip, notification } from 'antd';
import {
  AppstoreAddOutlined,
  CheckOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  DEPARTMENT_LABEL,
  MISSION_KIND_LABEL,
  MISSION_PERIOD_LABEL,
  MISSION_STATUS_OPTIONS,
  STAR_GROUP_LABEL,
  SYSTEM_MISSION_CODES,
  SYSTEM_MISSION_LABEL,
  labelOf,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { MissionAdmin, MissionStatus } from 'pages/star-shared/types';
import { TABLE_EMPTY, formatDate, missionLimitText } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import { Ellipsis, NoWrap } from 'pages/star-shared/components/status-tags';
import { colors } from 'theme/tokens';
import { useCurrentRoles } from 'hooks/use-current-roles';
import MissionModal, { AUTO_PAY_ADMIN_ONLY_HINT } from './mission-modal';
import RunMissionCard from './run-mission-card';
import TemplateModal from './template-modal';

export const isSystemMission = (m: MissionAdmin) =>
  !!m.isSystem || m.kind === 'AUTO' || SYSTEM_MISSION_CODES.includes(m.code);

const MissionsTab = () => {
  const { isAdmin } = useCurrentRoles();
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MissionAdmin | null>(null);
  const { items, loading, setItems } = usePagedList<MissionAdmin>(
    API.starsMissions,
    {},
    { refreshKey }
  );
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const changeStatus = async (mission: MissionAdmin, status: MissionStatus) => {
    setSavingId(mission.id);
    try {
      await AxiosUtils.patch(`${API.starsMissions}/${mission.id}`, { status });
      setItems((prev) => prev.map((m) => (m.id === mission.id ? { ...m, status } : m)));
      notification.success({ message: 'Đã đổi trạng thái nhiệm vụ.' });
    } catch (err) {
      notification.error({ message: mapStarError(err) });
      triggerRefresh();
    } finally {
      setSavingId(null);
    }
  };

  const columns: ColumnsType<MissionAdmin> = [
    // Bảng có scroll.x → table-layout: fixed: cột nào cũng có width (tổng = scroll.x), không thì
    // cột "Nhiệm vụ" chỉ còn phần dư ~110px và tên/mô tả gần như không đọc được.
    {
      title: 'Mã',
      key: 'code',
      width: 170,
      render: (_, m) => (
        <div>
          <Space size={4}>
            {isSystemMission(m) && (
              <Tooltip title="Nhiệm vụ hệ thống: chỉ bật/tắt, thưởng theo cấu hình.">
                <LockOutlined style={{ color: colors.textSecondary }} />
              </Tooltip>
            )}
            <code>{m.code}</code>
          </Space>
          {SYSTEM_MISSION_LABEL[m.code] && (
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>
              {SYSTEM_MISSION_LABEL[m.code]}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Nhiệm vụ',
      key: 'title',
      width: 280,
      render: (_, m) => (
        <div>
          <div style={{ fontWeight: 600 }}>{m.title}</div>
          <Ellipsis value={m.description} width={260} />
        </div>
      ),
    },
    {
      title: 'Loại',
      key: 'kind',
      width: 110,
      render: (_, m) => <Tag>{labelOf(MISSION_KIND_LABEL, m.kind)}</Tag>,
    },
    {
      title: 'Kỳ',
      key: 'period',
      width: 90,
      render: (_, m) => labelOf(MISSION_PERIOD_LABEL, m.period),
    },
    {
      title: 'Thưởng',
      key: 'rewardStars',
      width: 130,
      align: 'right',
      render: (_, m) =>
        isSystemMission(m) ? <span>Theo cấu hình</span> : <StarAmount value={m.rewardStars} />,
    },
    {
      title: 'Mục tiêu',
      dataIndex: 'target',
      key: 'target',
      width: 96,
      align: 'right',
      render: (v) => v ?? '—',
    },
    {
      // "15/tháng · 1/ngày · 2/tuần": lượt thưởng/kỳ + giới hạn LẦN NỘP /ngày, /tuần (doc 10 §4.1).
      title: 'Giới hạn',
      key: 'limits',
      width: 170,
      render: (_, m) => missionLimitText(m),
    },
    {
      title: 'Cần duyệt',
      key: 'needsApproval',
      width: 100,
      align: 'center',
      render: (_, m) =>
        m.needsApproval ? <CheckOutlined style={{ color: colors.successInk }} /> : '—',
    },
    {
      title: 'Đối tượng',
      key: 'audience',
      width: 190,
      render: (_, m) => {
        const groups = m.audience?.starGroups || [];
        const departments = m.audience?.departments || [];
        if (!groups.length && !departments.length) return <span>Tất cả</span>;
        return (
          <Space size={4} wrap>
            {groups.map((g) => (
              <Tag key={g} color="blue">
                {labelOf(STAR_GROUP_LABEL, g)}
              </Tag>
            ))}
            {departments.map((d) => (
              <Tag key={d}>{DEPARTMENT_LABEL[d] || d}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Hiệu lực',
      key: 'range',
      width: 190,
      // Hẹp thì xuống dòng sau "→", không bẻ giữa ngày.
      render: (_, m) =>
        m.startsAt || m.endsAt ? (
          <span>
            <NoWrap>{formatDate(m.startsAt)} →</NoWrap> <NoWrap>{formatDate(m.endsAt)}</NoWrap>
          </span>
        ) : (
          'Không giới hạn'
        ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 140,
      render: (_, m) => (
        <Select
          size="small"
          style={{ width: 120 }}
          value={m.status}
          options={MISSION_STATUS_OPTIONS}
          loading={savingId === m.id}
          disabled={savingId === m.id}
          onChange={(value) => changeStatus(m, value as MissionStatus)}
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_, m) => {
        // Nhiệm vụ tự trả thưởng (không cần duyệt): chỉ Admin sửa.
        const system = isSystemMission(m);
        const autoPayLocked = !system && !isAdmin && !m.needsApproval;
        let tip = 'Sửa';
        if (system) tip = 'Nhiệm vụ hệ thống: chỉ bật/tắt';
        else if (autoPayLocked) tip = AUTO_PAY_ADMIN_ONLY_HINT;
        return (
          <Tooltip title={tip}>
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={tip}
              disabled={system || autoPayLocked}
              onClick={() => setEditTarget(m)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Nhiệm vụ có ổ khoá là nhiệm vụ hệ thống (chấm công, kim cương, ca chuẩn, thâm niên, live, tuyển dụng, chấm nhảy…): chỉ bật/tắt ở đây, mức thưởng chỉnh ở Cấu hình. Nhiệm vụ tự báo cáo do bạn tạo (hoặc tạo từ mẫu) sẽ vào Hàng chờ duyệt khi có người nộp."
      />
      <RunMissionCard />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
        <CustomButton
          bgColor="third-green"
          text="Tạo từ mẫu"
          icon={<AppstoreAddOutlined />}
          onClick={() => setTemplateOpen(true)}
        />
        <CustomButton
          bgColor="secondary-green"
          text="Thêm nhiệm vụ"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={TABLE_EMPTY}
          // = tổng width cột: 170+280+110+90+130+96+170+100+190+190+140+100.
          scroll={{ x: 1766 }}
        />
      </div>
      <MissionModal
        open={createOpen}
        mission={null}
        onClose={() => setCreateOpen(false)}
        onSuccess={triggerRefresh}
      />
      <TemplateModal
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        onCreated={triggerRefresh}
      />
      <MissionModal
        open={!!editTarget}
        mission={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={triggerRefresh}
      />
    </div>
  );
};

export default MissionsTab;
